import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  NCR,
  NCRSerial,
  PartDefect,
  NCType,
  NCSubType,
  PartAttribute,
  SerialStatus,
  Operation,
  Machine,
  Department,
  User,
  SearchNCRsRequest,
  SearchNCRsResponse,
  CreateNCRRequest,
  AddDefectRequest,
  UpdateSerialDispositionRequest,
  NCRReportData
} from '../../services/ncrApi';
import { WorkOrder } from '../../services/workOrderApi';

export interface NCRPagination {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface NCRDropdowns {
  workOrders: WorkOrder[];
  operations: Operation[];
  serials: NCRSerial[];
  ncTypes: NCType[];
  ncSubTypes: NCSubType[];
  partAttributes: PartAttribute[];
  partSecAttributes: PartAttribute[];
  partTercAttributes: PartAttribute[];
  serialStatuses: SerialStatus[];
  machines: Machine[];
  departments: Department[];
  users: User[];
  rejectedSerials?: any[]; // Raw data from stored procedure
}

export interface NCRState {
  // List state
  ncrs: NCR[];
  loading: boolean;
  error: string | null;
  
  // Pagination
  pagination: NCRPagination;
  
  // Search and filters
  searchTerm: string;
  statusFilter: 'All' | 'Open' | 'Closed';
  workOrderFilter: string;
  dateFromFilter: string;
  dateToFilter: string;
  
  // Selected NCR for edit/view
  selectedNCR: NCR | null;
  selectedNCRLoading: boolean;
  selectedNCRError: string | null;
  
  // NCR Creation
  createNCRData: CreateNCRRequest | null;
  createNCRLoading: boolean;
  createNCRError: string | null;
  
  // Dropdown data
  dropdowns: NCRDropdowns;
  dropdownsLoading: boolean;
  dropdownsError: string | null;
  
  // Serial management
  selectedSerials: NCRSerial[];
  serialsLoading: boolean;
  serialsError: string | null;
  
  // Defect management
  selectedDefects: PartDefect[];
  defectsLoading: boolean;
  defectsError: string | null;
  editingDefect: PartDefect | null;
  defectDialogOpen: boolean;
  
  // Multi-step form state (Fill NCR)
  currentStep: number;
  showClosingTab: boolean;
  showStepTwo: boolean;
  showStepThree: boolean;
  
  // Validation
  validationErrors: string[];
  
  // Report generation
  reportData: NCRReportData | null;
  reportLoading: boolean;
  reportError: string | null;
  
  // Statistics
  statistics: {
    totalOpen: number;
    totalClosed: number;
    totalThisMonth: number;
    averageCloseTime: number;
  };
  
  // UI state
  selectedWorkOrderId: string | null;
  selectedOperationId: string | null;
  selectedNCTypeId: string | null;
  detailModalOpen: boolean;
  deleteDialogOpen: boolean;
  ncrToDelete: NCR | null;
  serialToDelete: NCRSerial | null;
}

const initialState: NCRState = {
  // List state
  ncrs: [],
  loading: false,
  error: null,
  
  // Pagination
  pagination: {
    currentPage: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0,
  },
  
  // Search and filters
  searchTerm: '',
  statusFilter: 'All',
  workOrderFilter: '',
  dateFromFilter: '',
  dateToFilter: '',
  
  // Selected NCR
  selectedNCR: null,
  selectedNCRLoading: false,
  selectedNCRError: null,
  
  // NCR Creation
  createNCRData: null,
  createNCRLoading: false,
  createNCRError: null,
  
  // Dropdown data
  dropdowns: {
    workOrders: [],
    operations: [],
    serials: [],
    ncTypes: [],
    ncSubTypes: [],
    partAttributes: [],
    partSecAttributes: [],
    partTercAttributes: [],
    serialStatuses: [],
    machines: [],
    departments: [],
    users: [],
  },
  dropdownsLoading: false,
  dropdownsError: null,
  
  // Serial management
  selectedSerials: [],
  serialsLoading: false,
  serialsError: null,
  
  // Defect management
  selectedDefects: [],
  defectsLoading: false,
  defectsError: null,
  editingDefect: null,
  defectDialogOpen: false,
  
  // Multi-step form
  currentStep: 1,
  showClosingTab: false,
  showStepTwo: false,
  showStepThree: false,
  
  // Validation
  validationErrors: [],
  
  // Report generation
  reportData: null,
  reportLoading: false,
  reportError: null,
  
  // Statistics
  statistics: {
    totalOpen: 0,
    totalClosed: 0,
    totalThisMonth: 0,
    averageCloseTime: 0,
  },
  
  // UI state
  selectedWorkOrderId: null,
  selectedOperationId: null,
  selectedNCTypeId: null,
  detailModalOpen: false,
  deleteDialogOpen: false,
  ncrToDelete: null,
  serialToDelete: null,
};

const ncrSlice = createSlice({
  name: 'ncr',
  initialState,
  reducers: {
    // List actions
    fetchNCRsStart: (state, action: PayloadAction<SearchNCRsRequest | undefined>) => {
      state.loading = true;
      state.error = null;
    },
    fetchNCRsSuccess: (state, action: PayloadAction<SearchNCRsResponse>) => {
      state.loading = false;
      state.ncrs = action.payload.items;
      state.pagination = {
        currentPage: action.payload.page_num,
        pageSize: action.payload.page_size,
        totalCount: action.payload.total_count,
        totalPages: Math.ceil(action.payload.total_count / action.payload.page_size),
      };
      state.error = null;
    },
    fetchNCRsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Search and filter actions
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.currentPage = 1;
    },
    setStatusFilter: (state, action: PayloadAction<'All' | 'Open' | 'Closed'>) => {
      state.statusFilter = action.payload;
      state.pagination.currentPage = 1;
    },
    setWorkOrderFilter: (state, action: PayloadAction<string>) => {
      state.workOrderFilter = action.payload;
      state.pagination.currentPage = 1;
    },
    setDateFromFilter: (state, action: PayloadAction<string>) => {
      state.dateFromFilter = action.payload;
      state.pagination.currentPage = 1;
    },
    setDateToFilter: (state, action: PayloadAction<string>) => {
      state.dateToFilter = action.payload;
      state.pagination.currentPage = 1;
    },
    
    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    
    // Selected NCR actions
    fetchNCRByIdStart: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = true;
      state.selectedNCRError = null;
    },
    fetchNCRByIdSuccess: (state, action: PayloadAction<NCR>) => {
      state.selectedNCRLoading = false;
      state.selectedNCR = action.payload;
      state.selectedNCRError = null;
      
      // Set step visibility based on NCR status
      state.showClosingTab = action.payload.what_txt ? true : false;
      state.showStepTwo = action.payload.responsible_sign ? true : false;
      state.showStepThree = action.payload.responsible_sign ? true : false;
    },
    fetchNCRByIdFailure: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = false;
      state.selectedNCRError = action.payload;
    },
    
    // Create NCR actions
    createNCRStart: (state, action: PayloadAction<CreateNCRRequest>) => {
      state.createNCRLoading = true;
      state.createNCRError = null;
      state.createNCRData = action.payload;
    },
    createNCRSuccess: (state, action: PayloadAction<NCR>) => {
      state.createNCRLoading = false;
      state.selectedNCR = action.payload;
      state.createNCRError = null;
    },
    createNCRFailure: (state, action: PayloadAction<string>) => {
      state.createNCRLoading = false;
      state.createNCRError = action.payload;
    },
    
    // Update NCR actions
    updateNCRStart: (state, action: PayloadAction<Partial<NCR>>) => {
      state.selectedNCRLoading = true;
      state.selectedNCRError = null;
    },
    updateNCRSuccess: (state, action: PayloadAction<NCR>) => {
      state.selectedNCRLoading = false;
      state.selectedNCR = action.payload;
      state.selectedNCRError = null;
      
      // Update in list if present
      const index = state.ncrs.findIndex(ncr => ncr.id === action.payload.id);
      if (index !== -1) {
        state.ncrs[index] = action.payload;
      }
    },
    updateNCRFailure: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = false;
      state.selectedNCRError = action.payload;
    },
    
    // Delete NCR actions
    deleteNCRStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deleteNCRSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.ncrs = state.ncrs.filter(ncr => ncr.id !== action.payload);
      state.pagination.totalCount -= 1;
      state.error = null;
    },
    deleteNCRFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Dropdown actions
    fetchDropdownsStart: (state) => {
      state.dropdownsLoading = true;
      state.dropdownsError = null;
    },
    fetchDropdownsSuccess: (state, action: PayloadAction<Partial<NCRDropdowns>>) => {
      state.dropdownsLoading = false;
      state.dropdowns = { ...state.dropdowns, ...action.payload };
      state.dropdownsError = null;
    },
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdownsLoading = false;
      state.dropdownsError = action.payload;
    },
    
    // Serial management actions
    fetchSerialsStart: (state, action: PayloadAction<string>) => {
      state.serialsLoading = true;
      state.serialsError = null;
    },
    fetchSerialsSuccess: (state, action: PayloadAction<NCRSerial[]>) => {
      state.serialsLoading = false;
      state.selectedSerials = action.payload;
      state.serialsError = null;
    },
    fetchSerialsFailure: (state, action: PayloadAction<string>) => {
      state.serialsLoading = false;
      state.serialsError = action.payload;
    },
    
    updateSerialStart: (state, action: PayloadAction<UpdateSerialDispositionRequest>) => {
      state.serialsLoading = true;
      state.serialsError = null;
    },
    updateSerialSuccess: (state, action: PayloadAction<NCRSerial>) => {
      state.serialsLoading = false;
      const index = state.selectedSerials.findIndex(serial => serial.id === action.payload.id);
      if (index !== -1) {
        state.selectedSerials[index] = action.payload;
      }
      state.serialsError = null;
    },
    updateSerialFailure: (state, action: PayloadAction<string>) => {
      state.serialsLoading = false;
      state.serialsError = action.payload;
    },
    
    deleteSerialStart: (state, action: PayloadAction<string>) => {
      state.serialsLoading = true;
      state.serialsError = null;
    },
    deleteSerialSuccess: (state, action: PayloadAction<string>) => {
      state.serialsLoading = false;
      state.selectedSerials = state.selectedSerials.filter(serial => serial.id !== action.payload);
      state.serialsError = null;
    },
    deleteSerialFailure: (state, action: PayloadAction<string>) => {
      state.serialsLoading = false;
      state.serialsError = action.payload;
    },
    
    // Defect management actions
    addDefectStart: (state, action: PayloadAction<AddDefectRequest>) => {
      state.defectsLoading = true;
      state.defectsError = null;
    },
    addDefectSuccess: (state, action: PayloadAction<PartDefect>) => {
      state.defectsLoading = false;
      state.selectedDefects.push(action.payload);
      state.defectsError = null;
      state.defectDialogOpen = false;
      state.editingDefect = null;
    },
    addDefectFailure: (state, action: PayloadAction<string>) => {
      state.defectsLoading = false;
      state.defectsError = action.payload;
    },
    
    deleteDefectStart: (state, action: PayloadAction<string>) => {
      state.defectsLoading = true;
      state.defectsError = null;
    },
    deleteDefectSuccess: (state, action: PayloadAction<string>) => {
      state.defectsLoading = false;
      state.selectedDefects = state.selectedDefects.filter(defect => defect.id !== action.payload);
      state.defectsError = null;
    },
    deleteDefectFailure: (state, action: PayloadAction<string>) => {
      state.defectsLoading = false;
      state.defectsError = action.payload;
    },
    
    // Step-wise NCR filling actions
    saveGeneralDetailsStart: (state, action: PayloadAction<{ ncrId: string; data: Partial<NCR> }>) => {
      state.selectedNCRLoading = true;
      state.selectedNCRError = null;
    },
    saveGeneralDetailsSuccess: (state, action: PayloadAction<NCR>) => {
      state.selectedNCRLoading = false;
      state.selectedNCR = action.payload;
      state.showClosingTab = true;
      state.selectedNCRError = null;
    },
    saveGeneralDetailsFailure: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = false;
      state.selectedNCRError = action.payload;
    },
    
    saveSerialsDispositionStart: (state, action: PayloadAction<{ ncrId: string; serialData: NCRSerial[] }>) => {
      state.selectedNCRLoading = true;
      state.selectedNCRError = null;
    },
    saveSerialsDispositionSuccess: (state, action: PayloadAction<NCR>) => {
      state.selectedNCRLoading = false;
      state.selectedNCR = action.payload;
      state.showStepThree = true;
      state.selectedNCRError = null;
    },
    saveSerialsDispositionFailure: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = false;
      state.selectedNCRError = action.payload;
    },
    
    saveClosingStart: (state, action: PayloadAction<{ ncrId: string; data: any }>) => {
      state.selectedNCRLoading = true;
      state.selectedNCRError = null;
    },
    saveClosingSuccess: (state, action: PayloadAction<NCR>) => {
      state.selectedNCRLoading = false;
      state.selectedNCR = action.payload;
      state.selectedNCRError = null;
    },
    saveClosingFailure: (state, action: PayloadAction<string>) => {
      state.selectedNCRLoading = false;
      state.selectedNCRError = action.payload;
    },
    
    // Report actions
    generateReportStart: (state, action: PayloadAction<string>) => {
      state.reportLoading = true;
      state.reportError = null;
    },
    generateReportSuccess: (state, action: PayloadAction<NCRReportData>) => {
      state.reportLoading = false;
      state.reportData = action.payload;
      state.reportError = null;
    },
    generateReportFailure: (state, action: PayloadAction<string>) => {
      state.reportLoading = false;
      state.reportError = action.payload;
    },
    
    // UI state actions
    setSelectedWorkOrderId: (state, action: PayloadAction<string | null>) => {
      state.selectedWorkOrderId = action.payload;
      if (!action.payload) {
        state.selectedOperationId = null;
        state.dropdowns.operations = [];
        state.dropdowns.serials = [];
      }
    },
    setSelectedOperationId: (state, action: PayloadAction<string | null>) => {
      state.selectedOperationId = action.payload;
      if (!action.payload) {
        state.dropdowns.serials = [];
      }
    },
    setSelectedNCTypeId: (state, action: PayloadAction<string | null>) => {
      state.selectedNCTypeId = action.payload;
      if (!action.payload) {
        state.dropdowns.ncSubTypes = [];
      }
    },
    
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    setEditingDefect: (state, action: PayloadAction<PartDefect | null>) => {
      state.editingDefect = action.payload;
    },
    setDefectDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.defectDialogOpen = action.payload;
      if (!action.payload) {
        state.editingDefect = null;
      }
    },
    
    setDetailModalOpen: (state, action: PayloadAction<boolean>) => {
      state.detailModalOpen = action.payload;
    },
    
    setDeleteDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.deleteDialogOpen = action.payload;
      if (!action.payload) {
        state.ncrToDelete = null;
        state.serialToDelete = null;
      }
    },
    setNCRToDelete: (state, action: PayloadAction<NCR | null>) => {
      state.ncrToDelete = action.payload;
    },
    setSerialToDelete: (state, action: PayloadAction<NCRSerial | null>) => {
      state.serialToDelete = action.payload;
    },
    
    // Statistics actions
    updateStatistics: (state, action: PayloadAction<{ totalOpen: number; totalClosed: number; totalThisMonth: number; averageCloseTime: number }>) => {
      state.statistics = action.payload;
    },
    
    // Clear errors
    clearError: (state) => {
      state.error = null;
      state.selectedNCRError = null;
      state.createNCRError = null;
      state.dropdownsError = null;
      state.serialsError = null;
      state.defectsError = null;
      state.reportError = null;
    },
    
    // Reset state
    resetNCRState: (state) => {
      state.selectedNCR = null;
      state.selectedSerials = [];
      state.selectedDefects = [];
      state.editingDefect = null;
      state.currentStep = 1;
      state.showClosingTab = false;
      state.showStepTwo = false;
      state.showStepThree = false;
      state.validationErrors = [];
      state.reportData = null;
      state.selectedWorkOrderId = null;
      state.selectedOperationId = null;
      state.selectedNCTypeId = null;
    },
  },
});

export const {
  // List actions
  fetchNCRsStart,
  fetchNCRsSuccess,
  fetchNCRsFailure,
  
  // Search and filter actions
  setSearchTerm,
  setStatusFilter,
  setWorkOrderFilter,
  setDateFromFilter,
  setDateToFilter,
  
  // Pagination actions
  setPage,
  setPageSize,
  
  // Selected NCR actions
  fetchNCRByIdStart,
  fetchNCRByIdSuccess,
  fetchNCRByIdFailure,
  
  // Create NCR actions
  createNCRStart,
  createNCRSuccess,
  createNCRFailure,
  
  // Update NCR actions
  updateNCRStart,
  updateNCRSuccess,
  updateNCRFailure,
  
  // Delete NCR actions
  deleteNCRStart,
  deleteNCRSuccess,
  deleteNCRFailure,
  
  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  
  // Serial management actions
  fetchSerialsStart,
  fetchSerialsSuccess,
  fetchSerialsFailure,
  updateSerialStart,
  updateSerialSuccess,
  updateSerialFailure,
  deleteSerialStart,
  deleteSerialSuccess,
  deleteSerialFailure,
  
  // Defect management actions
  addDefectStart,
  addDefectSuccess,
  addDefectFailure,
  deleteDefectStart,
  deleteDefectSuccess,
  deleteDefectFailure,
  
  // Step-wise NCR filling actions
  saveGeneralDetailsStart,
  saveGeneralDetailsSuccess,
  saveGeneralDetailsFailure,
  saveSerialsDispositionStart,
  saveSerialsDispositionSuccess,
  saveSerialsDispositionFailure,
  saveClosingStart,
  saveClosingSuccess,
  saveClosingFailure,
  
  // Report actions
  generateReportStart,
  generateReportSuccess,
  generateReportFailure,
  
  // UI state actions
  setSelectedWorkOrderId,
  setSelectedOperationId,
  setSelectedNCTypeId,
  setCurrentStep,
  setValidationErrors,
  clearValidationErrors,
  setEditingDefect,
  setDefectDialogOpen,
  setDetailModalOpen,
  setDeleteDialogOpen,
  setNCRToDelete,
  setSerialToDelete,
  
  // Statistics actions
  updateStatistics,
  
  // Clear errors
  clearError,
  
  // Reset state
  resetNCRState,
} = ncrSlice.actions;

export default ncrSlice.reducer; 